import { forwardRef, useState } from "react";
import { StyleSheet, Text, TextInput, type TextInputProps, View } from "react-native";

import { border, radius, spacing, surface, text, typography } from "@/src/theme/tokens";

type Props = TextInputProps & { label: string; hint?: string; error?: string };

export const AuthField = forwardRef<TextInput, Props>(function AuthField(
  { label, hint, error, onBlur, onFocus, style, ...props },
  ref
) {
  const [focused, setFocused] = useState(false);
  const supportingText = error ?? hint;
  return (
    <View style={styles.group}>
      <Text style={styles.label}>{label}</Text>
      <TextInput
        {...props}
        ref={ref}
        accessibilityLabel={label}
        cursorColor={text.brand}
        onBlur={(event) => { setFocused(false); onBlur?.(event); }}
        onFocus={(event) => { setFocused(true); onFocus?.(event); }}
        placeholderTextColor={text.secondary}
        selectionColor={text.brand}
        style={[styles.input, focused && styles.focused, Boolean(error) && styles.error, style]}
        underlineColorAndroid="transparent"
      />
      {supportingText ? (
        <Text accessibilityLiveRegion={error ? "polite" : "none"} style={[styles.hint, error && styles.errorText]}>
          {supportingText}
        </Text>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  group: { gap: spacing.sm },
  label: { color: text.primary, fontFamily: typography.family.medium, fontSize: 15, lineHeight: 21 },
  input: {
    backgroundColor: surface.primary,
    borderColor: border.subtle,
    borderRadius: radius.md,
    borderWidth: 1,
    color: text.primary,
    fontFamily: typography.family.regular,
    fontSize: typography.size.body,
    minHeight: 52,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  focused: { borderColor: border.brand, borderWidth: 2, paddingHorizontal: spacing.lg - 1, paddingVertical: spacing.md - 1 },
  error: { borderColor: border.danger },
  hint: { color: text.secondary, fontFamily: typography.family.regular, fontSize: typography.size.caption, lineHeight: typography.lineHeight.caption },
  errorText: { color: text.danger },
});
